import { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMapsLoading } from "../../administrativePanelPage/googleMapsHook";
import { AiOutlineClose, AiOutlineCheck } from "react-icons/ai";
import { toast } from "react-toastify";
import {
	ModalOverlay,
	ModalContainer,
	ModalHeader,
	ModalContent,
	MapContainer,
	ButtonsContainer,
} from "./style";

const containerStyle = {
	width: "100%",
	height: "400px",
};

const defaultOptions = {
	disableDefaultUI: false,
	zoomControl: true,
	streetViewControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
};

// Default position: São Paulo, Brazil
const DEFAULT_POSITION = {
	lat: -23.5505,
	lng: -46.6333,
};

export function MapModal({
	isOpen,
	onClose,
	onConfirm,
	initialPosition,
	addressDescription,
	isLoading = false,
}) {
	const { isLoaded } = useGoogleMapsLoading();
	const [selectedPosition, setSelectedPosition] = useState(
		initialPosition || DEFAULT_POSITION,
	);

	const mapRef = useRef();

	const onLoad = useCallback(
		(map) => {
			mapRef.current = map;

			// If modal is open and has initial position, center the map
			if (isOpen && initialPosition) {
				map.panTo(initialPosition);
				map.setZoom(18);
			}
		},
		[isOpen, initialPosition],
	);

	const onUnmount = useCallback(() => {
		mapRef.current = null;
	}, []);

	const handleMapClick = useCallback((event) => {
		setSelectedPosition({
			lat: event.latLng.lat(),
			lng: event.latLng.lng(),
		});
	}, []);

	const handleMarkerDragEnd = useCallback((event) => {
		setSelectedPosition({
			lat: event.latLng.lat(),
			lng: event.latLng.lng(),
		});
	}, []);

	const handleConfirm = useCallback(() => {
		if (!selectedPosition) {
			toast.error("Selecione uma localização no mapa");
			return;
		}

		onConfirm(selectedPosition);
	}, [selectedPosition, onConfirm]);

	// Reset position when modal opens
	useEffect(() => {
		if (isOpen) {
			const position = initialPosition || DEFAULT_POSITION;
			setSelectedPosition(position);
		}
	}, [isOpen, initialPosition]);

	// Center map when it's loaded and there's an initial position
	useEffect(() => {
		if (isOpen && mapRef.current && initialPosition) {
			// Small delay to ensure map is fully loaded
			setTimeout(() => {
				if (mapRef.current) {
					mapRef.current.panTo(initialPosition);
					mapRef.current.setZoom(18);
				}
			}, 100);
		}
	}, [isOpen, initialPosition]);

	if (!isOpen) return null;

	return (
		<ModalOverlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<div>
						<h3>Confirmar Localização</h3>
						<p>{addressDescription}</p>
					</div>
					<button type="button" onClick={onClose}>
						<AiOutlineClose />
					</button>
				</ModalHeader>

				<ModalContent>
					<div className="instructions">
						<p>Clique no mapa ou arraste o marcador para ajustar sua localização exata</p>
					</div>

					<MapContainer>
						{isLoaded ? (
							<GoogleMap
								mapContainerStyle={containerStyle}
								center={selectedPosition || DEFAULT_POSITION}
								zoom={initialPosition ? 18 : 10}
								onLoad={onLoad}
								onUnmount={onUnmount}
								onClick={handleMapClick}
								options={defaultOptions}
							>
								{selectedPosition && (
									<Marker
										position={selectedPosition}
										draggable={true}
										onDragEnd={handleMarkerDragEnd}
										title="Sua localização"
									/>
								)}
							</GoogleMap>
						) : (
							<div className="loading">
								<p>Carregando mapa...</p>
							</div>
						)}
					</MapContainer>

					<ButtonsContainer>
						<button
							type="button"
							className="cancel"
							onClick={onClose}
							disabled={isLoading}
						>
							Cancelar
						</button>
						<button
							type="button"
							className="confirm"
							onClick={handleConfirm}
							disabled={isLoading}
						>
							<AiOutlineCheck />
							{isLoading ? "Confirmando..." : "Confirmar Localização"}
						</button>
					</ButtonsContainer>
				</ModalContent>
			</ModalContainer>
		</ModalOverlay>
	);
}
